import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Code2, Globe, Settings, Palette, ArrowRight, CheckCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import './PublicPages.css';

const STATIC_SERVICES = [
  {
    icon: Code2, title: 'Software Development',
    desc: 'We architect and build robust, scalable custom software solutions tailored to your unique business requirements.',
    features: ['Web Applications', 'Mobile Apps', 'API Development', 'System Integration', 'Database Design', 'Cloud Solutions'],
    color: 'var(--secondary-color)'
  },
  {
    icon: Globe, title: 'Web Development',
    desc: 'From landing pages to full-stack web platforms, we create stunning digital experiences that convert visitors into customers.',
    features: ['React & Next.js', 'E-Commerce Platforms', 'CMS Integration', 'SEO Optimization', 'Performance Tuning', 'Responsive Design'],
    color: 'var(--info)'
  },
  {
    icon: Settings, title: 'System Maintenance',
    desc: 'Keep your systems running at peak performance with our comprehensive maintenance and support services.',
    features: ['24/7 Monitoring', 'Security Patches', 'Performance Audits', 'Backup Management', 'Incident Response', 'Version Updates'],
    color: 'var(--success)'
  },
  {
    icon: Palette, title: 'Graphic Design',
    desc: 'Elevate your brand with professional visual design that communicates your values and captivates your audience.',
    features: ['Brand Identity', 'UI/UX Design', 'Logo Design', 'Marketing Materials', 'Social Media Design', 'Pitch Decks'],
    color: 'var(--accent-light)'
  },
];

export default function ServicesPage() {
  const { data, loading } = useApp();
  const cms = data.websiteServices || [];

  const rows = useMemo(() => {
    if (cms.length === 0) return STATIC_SERVICES.map((s, i) => ({ kind: 'static', s, i }));
    return cms.map((s, i) => ({ kind: 'cms', s, i }));
  }, [cms]);

  return (
    <div className="services-page">
      <div style={{ padding: '120px 24px 80px', textAlign: 'center', background: 'var(--surface-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="section-tag">What We Offer</div>
        <h1 className="section-heading">Our Services</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '1rem', maxWidth: 560, margin: '0 auto' }}>
          {cms.length ? 'Managed from Admin → Website (Firestore).' : 'End-to-end digital solutions designed to accelerate your business growth and digital transformation.'}
        </p>
      </div>

      <div className="section-container" style={{ padding: '80px 24px' }}>
        {loading && <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Loading…</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {rows.map(({ kind, s, i }) => (
            kind === 'static' ? (
              <div key={s.title} className="card" style={{ padding: 36, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'center' }}>
                <div style={{ order: i % 2 === 0 ? 0 : 1 }}>
                  <div style={{ width: 56, height: 56, background: `${s.color}15`, border: `1px solid ${s.color}30`, borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color, marginBottom: 20 }}>
                    <s.icon size={26} />
                  </div>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 700, marginBottom: 14 }}>{s.title}</h2>
                  <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 24 }}>{s.desc}</p>
                  <Link to="/contact" className="btn-gold" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    Get Started <ArrowRight size={14} />
                  </Link>
                </div>
                <div style={{ order: i % 2 === 0 ? 1 : 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {s.features.map(f => (
                      <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 'var(--radius-md)', fontSize: '0.825rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                        <CheckCircle size={13} style={{ color: s.color, flexShrink: 0 }} />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div key={s.id || s.title} className="card" style={{ padding: 36 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                  {s.iconUrl ? (
                    <img src={s.iconUrl} alt="" style={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }} />
                  ) : (
                    <span style={{ fontSize: '2.5rem', lineHeight: 1 }}>{s.icon || '💡'}</span>
                  )}
                  <div style={{ flex: 1 }}>
                    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 700, marginBottom: 12 }}>{s.title}</h2>
                    <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 20 }}>{s.description}</p>
                    <Link to="/contact" className="btn-gold" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      Get Started <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </div>
    </div>
  );
}
